import type { Dispatch, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import type { CustomIntegrationAuthenticationType } from './WebhookContent';

export interface CustomWebhookInterface {
  id: string;
  name: string;
  baseUrl: string;
  authentication: {
    type: CustomIntegrationAuthenticationType;
    password: string;
    key?: string;
  };
  logo?: {
    name: string;
    domain: string;
  };
}

interface WebhooksContextType {
  chosenTab?: string;
  setChosenTab: (tab: string | undefined) => void;
  isTeamsInstallationOpen: boolean;
  setIsTeamsInstallationOpen: (open: boolean) => void;
  isWebhooksOpen: boolean;
  setWebhooks: Dispatch<SetStateAction<boolean>>;
  editWebhook?: Partial<CustomWebhookInterface>;
  setEditWebhook: (webhook?: Partial<CustomWebhookInterface>) => void;
}

const WebhooksContext = createContext<WebhooksContextType>({
  setChosenTab: () => {},
  isTeamsInstallationOpen: false,
  setIsTeamsInstallationOpen: () => {},
  isWebhooksOpen: false,
  setWebhooks: () => {},
  setEditWebhook: () => {},
});

export const WebhooksProvider = ({ children }: { children: React.ReactNode }) => {
  const [chosenTab, setChosenTab] = useState<string>();
  const [isTeamsInstallationOpen, setIsTeamsInstallationOpen] = useState(false);
  const [isWebhooksOpen, setWebhooks] = useState(false);
  const [editWebhook, setEditWebhook] = useState<Partial<CustomWebhookInterface>>();

  return (
    <WebhooksContext.Provider
      value={{
        chosenTab,
        setChosenTab,
        isTeamsInstallationOpen,
        setIsTeamsInstallationOpen,
        isWebhooksOpen,
        setWebhooks,
        editWebhook,
        setEditWebhook,
      }}
    >
      {children}
    </WebhooksContext.Provider>
  );
};

export const useWebhooksContext = () => {
  const {
    chosenTab,
    setChosenTab,
    isTeamsInstallationOpen,
    setIsTeamsInstallationOpen,
    isWebhooksOpen,
    setWebhooks,
    editWebhook,
    setEditWebhook,
  } = useContext(WebhooksContext);

  const openDialog = useCallback(
    (chosenTab: string) => {
      setChosenTab(chosenTab);
    },
    [setChosenTab],
  );

  const closeDialog = useCallback(() => {
    setChosenTab(undefined);
  }, [setChosenTab]);

  return {
    chosenTab,
    setChosenTab,
    openDialog,
    closeDialog,
    isOpen: chosenTab !== undefined,
    isTeamsInstallationOpen,
    setIsTeamsInstallationOpen,
    isWebhooksOpen,
    setWebhooks,
    editWebhook,
    setEditWebhook,
  };
};
